import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getFetch, postFetch } from '../../../../../fetch/fetch';
import { WinterRates, WinterBranch, WinterPeriod, WinterPeriodTarget, WinterBenefitType, isYmd, toDate, dayNum } from '../functions/winterprice';

//윈터스쿨 등록비 설정 — 금액(지점×기간) / 기간(대상·날짜) / 혜택(조건·할인율·적용 기간)
//디자인: winter.html 의 설정 다이얼로그(settingsVals)를 모달이 아닌 페이지로 옮김
//저장: POST /dashboard/payments/winterpay/rates/update (서버 normalizeWinterRates 와 같은 규칙으로 정리해서 보냄)

// ----- 상수 -----
const ACC = "#1976D2", ACC_SOFT = "#E3F2FD"; // 강조색 (달력과 통일)
const INVALID = "#D92D20";
const TARGETS: [WinterPeriodTarget, string][] = [["all", "전체"], ["student", "재학생"], ["general", "N수생·자퇴생"]];
const BENEFIT_TYPES: [WinterBenefitType, string][] = [["until", "마감일까지 결제하면"], ["on", "정해진 날 결제하면"], ["always", "항상 적용"]];
const BENEFIT_DESC: { [k: string]: string } = {
    until: "예: 조기 등록 — 11월 19일까지 결제한 학생",
    on: "예: 설명회 당일 등록 — 설명회 날에 결제한 학생",
    always: "결제일과 상관없이 모든 학생",
};
const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

// ----- 작은 함수 -----
const isValidPrice = (v: any) => Number.isInteger(v) && v > 0;
const digits = (v: any) => String(v ?? "").replace(/\D/g, "");
const comma = (v: any) => (v === "" || v == null ? "" : Number(v).toLocaleString("ko-KR"));
const pctReq = (v: any) => /^\d+$/.test(String(v)) && Number(v) >= 1 && Number(v) <= 100;
const baseOk = (v: string) => v !== "" && isValidPrice(Number(v));
const optPriceOk = (v: string | undefined | null) => v === "" || v == null || isValidPrice(Number(v));
const uid = (p: string) => p + Math.random().toString(36).slice(2, 8);
const pad2 = (n: number) => String(n).padStart(2, "0");
const ymdOf = (d: Date) => d.getFullYear() + "-" + pad2(d.getMonth() + 1) + "-" + pad2(d.getDate());
const daysInMonth = (y: number, m: number) => new Date(y, m + 1, 0).getDate();
const semOk = (r: { start: string; end: string }) => !!r && isYmd(r.start) && isYmd(r.end) && dayNum(toDate(r.start)) <= dayNum(toDate(r.end));
const mdKo = (str: string) => { if (!isYmd(str)) return '(날짜)'; const d = toDate(str); return (d.getMonth() + 1) + '월 ' + d.getDate() + '일'; };
const pName = (p: { name: string }) => String(p.name).trim() || '(이름 없음)';
const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v));

// ----- 편집용 초안 (입력칸은 문자열로 들고 있다가 저장 시 숫자로) -----
interface DraftPrice { base: string; periods: { [pid: string]: string }; }
interface DraftBenefit {
    id: string; name: string; type: WinterBenefitType; dates: string[]; pct: string;
    periodIds: string[]; excl: string[]; auto: boolean;
}
interface Draft { prices: { [b: string]: DraftPrice }; periods: WinterPeriod[]; benefits: DraftBenefit[]; }

const toDraft = (r: WinterRates, branchKeys: string[]): Draft => ({
    prices: Object.fromEntries(branchKeys.map(b => {
        const row = (r.prices && r.prices[b]) || { base: 0, periods: {} };
        return [b, {
            base: isValidPrice(row.base) ? String(row.base) : '',
            periods: Object.fromEntries(r.periods.map(p => {
                const v = row.periods ? row.periods[p.id] : null;
                return [p.id, v == null ? '' : String(v)];
            })),
        }];
    })),
    periods: r.periods.map(p => ({ ...p, ranges: p.ranges.map(x => ({ ...x })) })),
    benefits: r.benefits.map(c => ({
        id: c.id, name: c.name, type: c.type, dates: [...(c.dates || [])], pct: String(c.pct),
        periodIds: [...(c.periodIds || [])], excl: [...(c.excl || [])], auto: c.auto !== false,
    })),
});

//초안 → 서버 형식 (excl 비면 생략, auto 는 false 일 때만)
const fromDraft = (d: Draft): WinterRates => {
    const ids = new Set(d.periods.map(p => p.id));
    const prices: WinterRates["prices"] = {};
    Object.keys(d.prices).forEach(b => {
        const row = d.prices[b];
        const per: { [pid: string]: number | null } = {};
        d.periods.forEach(p => { const v = row.periods[p.id]; per[p.id] = v === '' || v == null ? null : Number(v); });
        prices[b] = { base: Number(row.base), periods: per };
    });
    return {
        prices,
        periods: d.periods.map(p => ({ id: p.id, name: String(p.name).trim(), target: p.target, ranges: p.ranges.map(x => ({ start: x.start, end: x.end })) })),
        benefits: d.benefits.map(c => {
            const dates = c.type === 'always' ? [] : c.type === 'until' ? [c.dates[0]] : Array.from(new Set(c.dates)).sort();
            const out: WinterRates["benefits"][number] = {
                id: c.id, name: String(c.name).trim(), type: c.type, dates, pct: Number(c.pct),
                periodIds: c.periodIds.filter(id => ids.has(id)),
            };
            if (c.excl.length) out.excl = [...c.excl];
            if (c.auto === false) out.auto = false;
            return out;
        }),
    };
};

const tabErrors = (d: Draft | null) => {
    if (!d) return { price: false, period: false, benefit: false };
    return {
        price: Object.values(d.prices).some(row => !baseOk(row.base) || d.periods.some(p => !optPriceOk(row.periods[p.id]))),
        period: d.periods.some(p => !String(p.name).trim() || !p.ranges.length || !p.ranges.every(semOk)),
        benefit: d.benefits.some(c => !String(c.name).trim() || !pctReq(c.pct) || (c.type !== 'always' && (!c.dates.length || !c.dates.every(isYmd)))),
    };
};

// ----- 공통 스타일 -----
const inputStyle = (border: string, extra?: React.CSSProperties): React.CSSProperties => ({
    height: 48, width: '100%', minWidth: 0, padding: '0 16px', border: `1px solid ${border}`, borderRadius: 8,
    fontFamily: 'inherit', fontSize: 15, color: 'var(--ink-900)', letterSpacing: '-0.02em', background: '#fff', boxSizing: 'border-box', ...extra,
});
const ddBtnStyle = (border: string, color: string, cursor: string): React.CSSProperties => ({
    height: 48, width: '100%', minWidth: 0, padding: '0 44px 0 16px', border: `1px solid ${border}`, borderRadius: 8, background: '#fff',
    fontFamily: 'inherit', fontSize: 15, color, letterSpacing: '-0.02em', textAlign: 'left', cursor, display: 'flex', alignItems: 'center',
    boxSizing: 'border-box', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
});
const iconBtn: React.CSSProperties = {
    width: 48, height: 48, borderRadius: '50%', border: 'none', background: 'none', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
};
const textBtn: React.CSSProperties = {
    alignSelf: 'flex-start', height: 40, padding: '0 12px 0 8px', borderRadius: 8, border: 'none', background: 'none', color: 'var(--ink-700)',
    fontFamily: 'inherit', fontSize: 14, fontWeight: 500, letterSpacing: '-0.02em', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
};
const dashedBtn: React.CSSProperties = {
    marginTop: 16, width: '100%', height: 48, padding: '0 24px', borderRadius: 8, border: '1px dashed var(--ink-300)', background: '#fff',
    color: 'var(--ink-700)', fontFamily: 'inherit', fontSize: 15, fontWeight: 500, letterSpacing: '-0.02em', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
};
const plainBtn: React.CSSProperties = {
    height: 48, padding: '0 24px', borderRadius: 8, border: '1px solid var(--ink-200)', background: '#fff', color: 'var(--ink-900)',
    fontFamily: 'inherit', fontSize: 15, fontWeight: 500, letterSpacing: '-0.02em', cursor: 'pointer',
};
const chipColors = (active: boolean) => ({ border: active ? ACC : 'var(--ink-200)', bg: active ? ACC_SOFT : '#fff', color: active ? ACC : 'var(--ink-900)' });
const chipStyle = (active: boolean, h: number, fs: number, px: number): React.CSSProperties => {
    const c = chipColors(active);
    return {
        height: h, padding: `0 ${px}px`, borderRadius: 8, border: `1px solid ${c.border}`, background: c.bg, color: c.color,
        fontFamily: 'inherit', fontSize: fs, fontWeight: 500, letterSpacing: '-0.02em', cursor: 'pointer',
    };
};
const listboxStyle: React.CSSProperties = {
    position: 'absolute', left: 0, right: 0, minWidth: 300, top: 'calc(100% + 4px)', zIndex: 30, maxHeight: 288, overflowY: 'auto',
    background: '#fff', border: '1px solid var(--ink-200)', borderRadius: 8, padding: 4, boxShadow: '0 12px 32px rgba(15, 23, 42, 0.14)',
    boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 2,
};

//style-hover 대체 (인라인 스타일보다 우선하도록 !important)
const HOVER_CSS = `
.wrp-root .h-border:hover:not(:disabled) { border-color: var(--ink-900) !important; }
.wrp-root .h-soft:hover:not(:disabled) { background: var(--bg-soft) !important; }
.wrp-root .h-softink:hover:not(:disabled) { background: var(--bg-soft) !important; color: var(--ink-900) !important; }
.wrp-root .h-dashed:hover:not(:disabled) { border-color: var(--ink-900) !important; color: var(--ink-900) !important; background: var(--bg-soft) !important; }
.wrp-root .h-day:hover:not(:disabled) { box-shadow: inset 0 0 0 1px var(--ink-300); }
.wrp-root .h-save:hover:not(:disabled) { background: #1565C0 !important; border-color: #1565C0 !important; }
.wrp-root input:read-only { cursor: default; }
`;

// ----- 아이콘 -----
const Chevron: React.FC<{ rot?: boolean; stroke?: string; style?: React.CSSProperties }> = ({ rot, stroke = 'var(--ink-500)', style }) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="1.5" style={{ transform: rot ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s ease', ...style }}><path d="m6 9 6 6 6-6"></path></svg>
);
const XIcon = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--ink-700)" strokeWidth="1.5"><path d="M18 6 6 18"></path><path d="m6 6 12 12"></path></svg>);
const PlusIcon: React.FC<{ size?: number }> = ({ size = 16 }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 5v14"></path><path d="M5 12h14"></path></svg>);
const CheckSvg: React.FC<{ visible: boolean; stroke?: string; size?: number; sw?: string }> = ({ visible, stroke = '#fff', size = 14, sw = '2' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={sw} style={{ flex: 'none', visibility: visible ? 'visible' : 'hidden' }}><path d="M20 6 9 17l-5-5"></path></svg>
);

// ----- 커스텀 드롭다운 (칸과 같은 모양의 목록) -----
interface DDOpt { value: string; label: string; desc?: string; }
const Dropdown: React.FC<{
    open: boolean; onToggle: () => void; value: string | null; options: DDOpt[]; onPick: (v: string) => void;
    placeholder?: string; disabled?: boolean;
}> = ({ open, onToggle, value, options, onPick, placeholder, disabled }) => {
    const cur = options.find(o => o.value === value);
    return (
        <div data-dd="" style={{ position: 'relative', minWidth: 0 }}>
            <button type="button" className="h-border" onClick={() => { if (!disabled) onToggle(); }} aria-haspopup="listbox" aria-expanded={open}
                style={ddBtnStyle(open ? ACC : 'var(--ink-200)', cur ? 'var(--ink-900)' : 'var(--ink-300)', disabled ? 'default' : 'pointer')}>
                {cur ? cur.label : (placeholder || '')}
            </button>
            <Chevron rot={open} style={{ position: 'absolute', right: 16, top: 16, pointerEvents: 'none' }} />
            {open &&
                <div role="listbox" style={listboxStyle}>
                    {options.map(o => {
                        const sel = o.value === value;
                        return (
                            <button key={o.value} type="button" role="option" aria-selected={sel} className="h-soft" onClick={() => onPick(o.value)}
                                style={{
                                    width: '100%', minHeight: 44, flex: 'none', padding: '0 12px', border: 'none', borderRadius: 6, background: sel ? ACC_SOFT : 'transparent',
                                    fontFamily: 'inherit', fontSize: 15, fontWeight: sel ? 500 : 400, color: 'var(--ink-900)', letterSpacing: '-0.02em', textAlign: 'left',
                                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                                }}>
                                <span style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '8px 0' }}>
                                    <span>{o.label}</span>
                                    {!!o.desc && <span style={{ fontSize: 13, fontWeight: 400, color: 'var(--ink-500)' }}>{o.desc}</span>}
                                </span>
                                <CheckSvg visible={sel} stroke={ACC} size={16} sw="1.75" />
                            </button>
                        );
                    })}
                </div>
            }
        </div>
    );
};

// ----- 커스텀 날짜 선택 (칸 아래에 한 달 달력) -----
const DatePicker: React.FC<{
    open: boolean; onToggle: () => void; value: string; onPick: (v: string) => void; baseBorder: string; disabled?: boolean;
}> = ({ open, onToggle, value, onPick, baseBorder, disabled }) => {
    const valid = isYmd(value);
    const [view, setView] = useState<{ y: number; m: number }>(() => { const d = valid ? toDate(value) : new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
    //열 때마다 선택된 날짜(없으면 오늘)의 달로 맞춤
    useEffect(() => {
        if (open) { const d = isYmd(value) ? toDate(value) : new Date(); setView({ y: d.getFullYear(), m: d.getMonth() }); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
    const shift = (n: number) => setView(v => { const d = new Date(v.y, v.m + n, 1); return { y: d.getFullYear(), m: d.getMonth() }; });
    const navBtn: React.CSSProperties = { width: 36, height: 36, borderRadius: '50%', border: 'none', background: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 };

    const cells: ({ day: number; ymd: string; sel: boolean; today: boolean; sun: boolean } | null)[] = [];
    if (open) {
        const first = new Date(view.y, view.m, 1).getDay(), days = daysInMonth(view.y, view.m);
        const todayN = dayNum(new Date()), selN = valid ? dayNum(toDate(value)) : null;
        for (let i = 0; i < first; i++) cells.push(null);
        for (let d = 1; d <= days; d++) {
            const dt = new Date(view.y, view.m, d), n = dayNum(dt);
            cells.push({ day: d, ymd: ymdOf(dt), sel: n === selN, today: n === todayN, sun: (first + d - 1) % 7 === 0 });
        }
        while (cells.length < 42) cells.push(null); // 항상 6주 고정
    }

    return (
        <div data-dd="" style={{ position: 'relative', minWidth: 0 }}>
            <button type="button" className="h-border" onClick={() => { if (!disabled) onToggle(); }} aria-haspopup="dialog" aria-expanded={open}
                style={{ ...ddBtnStyle(open ? ACC : baseBorder, valid ? 'var(--ink-900)' : 'var(--ink-300)', disabled ? 'default' : 'pointer'), textOverflow: 'clip' }}>
                {valid ? value.replace(/-/g, '. ') : '날짜 선택'}
            </button>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--ink-500)" strokeWidth="1.5" style={{ position: 'absolute', right: 16, top: 16, pointerEvents: 'none' }}><rect x="3" y="4" width="18" height="18" rx="2"></rect><path d="M16 2v4"></path><path d="M8 2v4"></path><path d="M3 10h18"></path></svg>
            {open &&
                <div role="dialog" aria-label="날짜 선택" style={{ position: 'absolute', left: 0, top: 'calc(100% + 4px)', zIndex: 30, width: 296, background: '#fff', border: '1px solid var(--ink-200)', borderRadius: 8, padding: 12, boxShadow: '0 12px 32px rgba(15, 23, 42, 0.14)', boxSizing: 'border-box' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 36, marginBottom: 4 }}>
                        <button type="button" className="h-soft" onClick={() => shift(-1)} style={navBtn}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--ink-900)" strokeWidth="1.5"><path d="m15 18-6-6 6-6"></path></svg></button>
                        <span style={{ fontSize: 15, fontWeight: 400, color: 'var(--ink-900)' }}>{view.y + '년 ' + (view.m + 1) + '월'}</span>
                        <button type="button" className="h-soft" onClick={() => shift(1)} style={navBtn}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--ink-900)" strokeWidth="1.5"><path d="m9 18 6-6-6-6"></path></svg></button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}>
                        {WEEKDAYS.map(wd => <span key={wd} style={{ height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: 'var(--ink-500)' }}>{wd}</span>)}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', rowGap: 2 }}>
                        {cells.map((c, i) => (
                            <div key={i} style={{ height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <button type="button" className="h-day" disabled={!c} onClick={() => { if (c) onPick(c.ymd); }}
                                    style={{
                                        flex: 'none', width: '100%', maxWidth: 36, height: 'auto', aspectRatio: '1 / 1', borderRadius: '50%',
                                        border: `1px solid ${!c ? 'transparent' : c.sel ? ACC : c.today ? 'var(--ink-700)' : 'transparent'}`,
                                        background: c && c.sel ? ACC : 'transparent',
                                        color: !c ? 'transparent' : c.sel ? '#fff' : c.sun ? 'var(--ink-400)' : 'var(--ink-900)',
                                        fontFamily: 'inherit', fontSize: 14, fontWeight: c && c.sel ? 500 : 400, letterSpacing: '-0.02em', cursor: 'pointer', padding: 0,
                                        visibility: c ? 'visible' : 'hidden', boxSizing: 'border-box',
                                    }}>{c ? c.day : ''}</button>
                            </div>
                        ))}
                    </div>
                </div>
            }
        </div>
    );
};

// ----- 체크박스 (금액 탭 지점 선택) -----
const CheckBox: React.FC<{ on: boolean; show: boolean; onClick: () => void }> = ({ on, show, onClick }) => (
    <button type="button" role="checkbox" aria-checked={on} onClick={onClick}
        style={{
            display: show ? 'flex' : 'none', width: 20, height: 20, flex: 'none', padding: 0, boxSizing: 'border-box', borderRadius: 4,
            border: `1px solid ${on ? ACC : 'var(--ink-300)'}`, background: on ? ACC : '#fff', cursor: 'pointer', alignItems: 'center', justifyContent: 'center',
        }}><CheckSvg visible={on} /></button>
);

type TabKey = 'price' | 'period' | 'benefit';

const WinterRegisterPrice: React.FC<any> = (props) => {
    const rootRef = useRef<HTMLDivElement>(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState<WinterRates | null>(null);         // 마지막으로 불러온/저장한 값
    const [defaults, setDefaults] = useState<WinterRates | null>(null);   // 초기값(서버 시드)
    const [branches, setBranches] = useState<WinterBranch[]>([]);
    const [fromDb, setFromDb] = useState(true);
    const [updatedByName, setUpdatedByName] = useState<string | null>(null);
    const [updatedAt, setUpdatedAt] = useState<string | null>(null);
    const [canEdit, setCanEdit] = useState(false);
    const [draft, setDraft] = useState<Draft | null>(null);

    // 화면 상태
    const [tab, setTab] = useState<TabKey>('price');
    const [openDd, setOpenDd] = useState<string | null>(null);
    const [showPast, setShowPast] = useState(false);
    const [pricePid, setPricePid] = useState<string | null>(null);
    const [fillVal, setFillVal] = useState('');
    const [priceView, setPriceView] = useState<'one' | 'all'>('one');
    const [priceSel, setPriceSel] = useState<{ [b: string]: boolean }>({});
    const [exclOpen, setExclOpen] = useState<string | null>(null);
    const [tipKey, setTipKey] = useState<string | null>(null);

    //지점 목록: 서버 branches 순서 + 금액표에만 있는 지점(있다면)
    const branchKeys = useCallback((list: WinterBranch[], r: WinterRates | null) => {
        const keys = list.map(b => b.english);
        if (r && r.prices) Object.keys(r.prices).forEach(k => { if (!keys.includes(k)) keys.push(k); });
        return keys;
    }, []);

    const load = useCallback(async (resetDraft: boolean) => {
        try {
            const result: any = await getFetch("/dashboard/payments/winterpay/rates/admin");
            if (!result || result.message !== "success") { setLoadError(true); toast.error("윈터 금액표를 불러오지 못했어요"); return; }
            const list: WinterBranch[] = result.branches || [];
            setBranches(list);
            setSaved(result.rates);
            setDefaults(result.defaultRates);
            setFromDb(!!result.fromDb);
            setUpdatedByName(result.updatedByName || null);
            setUpdatedAt(result.updatedAt || null);
            setCanEdit(!!result.canEdit);
            if (resetDraft) setDraft(toDraft(result.rates, branchKeys(list, result.rates)));
            setLoadError(false);
        } catch (e) {
            console.log(e);
            setLoadError(true);
            toast.error("윈터 금액표를 불러오지 못했어요");
        } finally {
            setLoading(false);
        }
    }, [branchKeys]);

    useEffect(() => { load(true); }, [load]);

    //바깥 클릭 / Esc 로 드롭다운·달력 닫기
    useEffect(() => {
        const onDown = (e: MouseEvent) => {
            const t = e.target as HTMLElement;
            if (!(t && t.closest && t.closest('[data-dd]'))) setOpenDd(null);
        };
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpenDd(null); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
    }, []);

    const korOf = useMemo(() => {
        const m: { [e: string]: string } = {};
        branches.forEach(b => { m[b.english] = b.korean; });
        return (e: string) => m[e] || e;
    }, [branches]);

    //초안 수정 (권한 없으면 무시)
    const upd = (fn: (x: Draft) => void) => {
        if (!canEdit) return;
        setDraft(prev => { if (!prev) return prev; const x = clone(prev); fn(x); return x; });
    };
    const toggleDd = (key: string) => setOpenDd(k => (k === key ? null : key));
    const pick = (fn: (x: Draft) => void) => { setOpenDd(null); upd(fn); };

    const d = draft;
    const err = tabErrors(d);
    const ok = !!d && !err.price && !err.period && !err.benefit;
    const bd = (good: any) => (good ? 'var(--ink-200)' : INVALID);
    const periods = d ? d.periods : [];
    const bKeys = d ? Object.keys(d.prices) : [];
    const todayN = dayNum(new Date());
    const isPastP = (p: WinterPeriod) => p.ranges.length > 0 && p.ranges.every(r => semOk(r) && dayNum(toDate(r.end)) < todayN);
    const pastFlags = periods.map(isPastP);
    const nPast = pastFlags.filter(Boolean).length;

    // ===== 금액 탭 =====
    const order = periods.map((p, i) => ({ p, i })).sort((a, b) => Number(pastFlags[a.i]) - Number(pastFlags[b.i]));
    const pid = periods.some(p => p.id === pricePid) ? pricePid : (order[0] ? order[0].p.id : null);
    const sp = periods.find(p => p.id === pid) || null;
    const rng = (p: WinterPeriod) => { const r = p.ranges.filter(semOk); if (!r.length) return '날짜 미입력'; const f = (v: string) => v.slice(2).replace(/-/g, '.'); return r.map(x => f(x.start) + ' ~ ' + f(x.end)).join(', '); };
    const showChk = priceView !== 'all' && !!sp;
    const picked = bKeys.filter(b => priceSel[b]);
    const allOn = bKeys.length > 0 && picked.length === bKeys.length;
    const fillOk = !!sp && isValidPrice(Number(fillVal));
    const fillTargets = picked.length ? picked : bKeys;
    const applyFill = () => {
        if (!fillOk || !sp) return;
        const v = fillVal;
        upd(x => { fillTargets.forEach(b => { x.prices[b].periods[sp.id] = v; }); });
    };
    //가격 표 열: 기간별 = 기본 + 선택 기간 / 전체 보기 = 기본 + 모든 기간
    const priceColPeriods: { p: WinterPeriod; i: number }[] = priceView === 'all' ? order : (sp ? [{ p: sp, i: periods.indexOf(sp) }] : []);
    const firstCol = showChk ? 128 : 96;
    const priceGrid = priceView === 'all'
        ? `${firstCol}px repeat(${1 + order.length}, minmax(112px, 1fr))`
        : `${firstCol}px repeat(${sp ? 2 : 1}, minmax(0, 1fr))`;
    const priceMinW = priceView === 'all' ? (96 + (1 + order.length) * 120) + 'px' : '0';
    const priceCols = priceView === 'all'
        ? ['지점', '기본*'].concat(order.map(({ p, i }) => pName(p) + (pastFlags[i] ? ' (지난)' : '')))
        : ['지점', '기본 월 금액*'].concat(sp ? [pName(sp) + ' 월 금액'] : []);
    const focusCell = (r: number, c: number) => {
        const el = rootRef.current?.querySelector('[data-pcell="' + r + ':' + c + '"]') as HTMLInputElement | null;
        if (el) { el.focus(); el.select(); }
    };
    const cellKey = (ri: number, ci: number) => (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' || e.key === 'ArrowDown') { e.preventDefault(); focusCell(ri + (e.key === 'Enter' && e.shiftKey ? -1 : 1), ci); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); focusCell(ri - 1, ci); }
    };

    // ===== 오류 안내 (무엇이 잘못됐는지 문장으로) =====
    const errMsgs = useMemo(() => {
        const msgs: string[] = [];
        if (!d) return msgs;
        const nm = (x: { name: string }, f: string) => '"' + (String(x.name).trim() || f) + '"';
        const keys = Object.keys(d.prices);
        const noBase = keys.filter(b => !baseOk(d.prices[b].base));
        if (noBase.length) msgs.push('[금액] 기본 월 금액을 입력해주세요: ' + noBase.map(korOf).join(', '));
        if (keys.some(b => d.periods.some(p => !optPriceOk(d.prices[b].periods[p.id])))) msgs.push('[금액] 기간 금액은 1원 이상 숫자로 입력하거나 비워 두세요');
        d.periods.forEach(p => {
            if (!String(p.name).trim()) msgs.push('[기간] 이름이 없는 기간이 있어요');
            if (!p.ranges.length || !p.ranges.every(semOk)) msgs.push('[기간] ' + nm(p, '이름 없는 기간') + ' 날짜를 확인해주세요 (시작일이 종료일보다 늦을 수 없어요)');
        });
        d.benefits.forEach(c => {
            if (!String(c.name).trim()) msgs.push('[혜택] 이름이 없는 혜택이 있어요');
            if (!pctReq(c.pct)) msgs.push('[혜택] ' + nm(c, '이름 없는 혜택') + ' 할인율은 1~100 사이로 입력해주세요');
            if (c.type !== 'always' && (!c.dates.length || !c.dates.every(isYmd))) msgs.push('[혜택] ' + nm(c, '이름 없는 혜택') + ' 날짜를 입력해주세요');
        });
        return Array.from(new Set(msgs));
    }, [d, korOf]);

    const periodsText = (ids: string[]) => {
        if (!ids.length) return '등록 기간 전체';
        return ids.map(id => { const p = periods.find(x => x.id === id); return p ? pName(p) : null; }).filter(Boolean).join('·');
    };

    // ===== 저장 / 되돌리기 =====
    const save = async () => {
        if (!d || !ok || !canEdit || saving) return;
        if (!window.confirm("윈터 금액표를 저장할까요? 고객 결제 화면 금액이 바로 바뀌어요.")) return;
        setSaving(true);
        try {
            const result: any = await postFetch("/dashboard/payments/winterpay/rates/update", { rates: fromDraft(d) });
            if (result && result.message === "success") {
                toast.success("윈터 금액표를 저장했어요");
                setSaved(result.rates);
                setDraft(toDraft(result.rates, branchKeys(branches, result.rates)));
                load(false); //마지막 수정자·시각 갱신
            } else if (result && result.message === "NOT_AUTHORIZED") {
                toast.error("수정 권한이 없어요");
            } else if (result && result.message === "invalid") {
                toast.error("저장하지 못했어요: " + (result.error || "입력값을 확인해주세요"));
            } else {
                toast.error("저장하지 못했어요. 잠시 후 다시 시도해주세요");
            }
        } catch (e) {
            console.log(e);
            toast.error("저장하지 못했어요. 잠시 후 다시 시도해주세요");
        } finally {
            setSaving(false);
        }
    };
    const resetToDefault = () => { if (!canEdit || !defaults) return; setDraft(toDraft(defaults, branchKeys(branches, defaults))); setOpenDd(null); };
    const cancelEdit = () => { if (!saved) return; setDraft(toDraft(saved, branchKeys(branches, saved))); setOpenDd(null); setFillVal(''); };

    const updatedText = (() => {
        if (!updatedAt) return null;
        const t = new Date(updatedAt);
        if (isNaN(t.getTime())) return null;
        return (t.getMonth() + 1) + '/' + t.getDate() + ' ' + pad2(t.getHours()) + ':' + pad2(t.getMinutes());
    })();

    const ro = !canEdit;
    const saveOn = ok && canEdit && !saving;

    // ===== 기간 블록 =====
    const periodGrid = '24px minmax(0, 1fr) minmax(0, 180px) 48px 48px 48px';
    const renderPeriod = (p: WinterPeriod, i: number, no: number) => (
        <div key={p.id} style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '16px 0', borderBottom: '1px solid var(--hair)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: periodGrid, gap: 8, alignItems: 'center' }}>
                <span style={{ fontSize: 14, color: 'var(--ink-500)' }}>{no}</span>
                <input value={p.name} readOnly={ro} onChange={e => { const v = e.target.value; upd(x => { x.periods[i].name = v; }); }} placeholder="기간 이름" style={inputStyle(bd(String(p.name).trim()))} />
                <Dropdown open={openDd === 'target:' + p.id} onToggle={() => toggleDd('target:' + p.id)} value={p.target} disabled={ro}
                    options={TARGETS.map(([value, label]) => ({ value, label }))}
                    onPick={v => pick(x => { x.periods[i].target = v as WinterPeriodTarget; })} />
                <button type="button" className="h-soft" title="위로" aria-label="위로" disabled={ro}
                    onClick={() => upd(x => { if (i > 0) [x.periods[i - 1], x.periods[i]] = [x.periods[i], x.periods[i - 1]]; })}
                    style={{ ...iconBtn, opacity: i === 0 ? 0.25 : 1, pointerEvents: i === 0 ? 'none' : 'auto' }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--ink-700)" strokeWidth="1.5"><path d="m18 15-6-6-6 6"></path></svg></button>
                <button type="button" className="h-soft" title="아래로" aria-label="아래로" disabled={ro}
                    onClick={() => upd(x => { if (i < x.periods.length - 1) [x.periods[i + 1], x.periods[i]] = [x.periods[i], x.periods[i + 1]]; })}
                    style={{ ...iconBtn, opacity: i === periods.length - 1 ? 0.25 : 1, pointerEvents: i === periods.length - 1 ? 'none' : 'auto' }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--ink-700)" strokeWidth="1.5"><path d="m6 9 6 6 6-6"></path></svg></button>
                <button type="button" className="h-soft" title="기간 삭제" aria-label="기간 삭제" disabled={ro}
                    onClick={() => upd(x => {
                        const id = x.periods[i].id;
                        x.periods.splice(i, 1);
                        Object.values(x.prices).forEach(r => { delete r.periods[id]; });
                        x.benefits.forEach(c => { c.periodIds = c.periodIds.filter(v => v !== id); });
                    })}
                    style={iconBtn}><XIcon /></button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {p.ranges.map((rg, j) => (
                    <div key={j} style={{ display: 'grid', gridTemplateColumns: periodGrid, gap: 8, alignItems: 'center' }}>
                        <div style={{ gridColumn: '2 / 4', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)', gap: 8, alignItems: 'center' }}>
                            <DatePicker open={openDd === 'rs:' + p.id + ':' + j} onToggle={() => toggleDd('rs:' + p.id + ':' + j)} value={rg.start} baseBorder={bd(semOk(rg))} disabled={ro}
                                onPick={v => pick(x => { x.periods[i].ranges[j].start = v; })} />
                            <span style={{ fontSize: 15, color: 'var(--ink-500)' }}>~</span>
                            <DatePicker open={openDd === 're:' + p.id + ':' + j} onToggle={() => toggleDd('re:' + p.id + ':' + j)} value={rg.end} baseBorder={bd(semOk(rg))} disabled={ro}
                                onPick={v => pick(x => { x.periods[i].ranges[j].end = v; })} />
                        </div>
                        <button type="button" className="h-soft" title="날짜 삭제" aria-label="날짜 삭제" disabled={ro}
                            onClick={() => upd(x => { x.periods[i].ranges.splice(j, 1); })}
                            style={{ ...iconBtn, gridColumn: 6, visibility: p.ranges.length > 1 && !ro ? 'visible' : 'hidden' }}><XIcon /></button>
                    </div>
                ))}
                {!ro &&
                    <button type="button" className="h-softink" onClick={() => upd(x => { x.periods[i].ranges.push({ start: '', end: '' }); })} style={{ ...textBtn, marginLeft: 32 }}><PlusIcon />날짜 추가</button>
                }
            </div>
        </div>
    );

    // ===== 혜택 블록 =====
    const renderBenefit = (c: DraftBenefit, i: number) => {
        const pctN = pctReq(c.pct) ? Number(c.pct) + '%' : '(할인율)';
        const where = periodsText(c.periodIds);
        const explain = (c.type === 'always'
            ? '→ 모든 학생, ' + where + ' 날짜에 ' + pctN + ' 할인'
            : c.type === 'until'
                ? '→ 결제일이 ' + mdKo(c.dates[0]) + '까지면 ' + where + ' 날짜에 ' + pctN + ' 할인'
                : '→ 결제일이 ' + c.dates.map(mdKo).join(', ') + ' 중 하나면 ' + where + ' 날짜에 ' + pctN + ' 할인')
            + (c.excl.length ? ' (제외: ' + c.excl.map(korOf).join('·') + ')' : '')
            + (c.auto === false ? ' · 직접 적용' : '');
        const autoNote = c.auto !== false ? '조건에 맞으면 발송 화면에서 자동으로 체크되고, 직원이 끌 수 없어요' : '발송 화면에서 직원이 체크해야 적용돼요';
        const tipOn = tipKey === 'auto-' + c.id;
        const exOpen = exclOpen === c.id;
        const label72: React.CSSProperties = { fontSize: 14, color: 'var(--ink-700)', width: 72, flex: 'none' };
        return (
            <div key={c.id} style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '16px 0', borderBottom: '1px solid var(--hair)' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 220px) 48px', gap: 8, alignItems: 'center' }}>
                    <input value={c.name} readOnly={ro} onChange={e => { const v = e.target.value; upd(x => { x.benefits[i].name = v; }); }} placeholder="혜택 이름" style={inputStyle(bd(String(c.name).trim()))} />
                    <Dropdown open={openDd === 'btype:' + c.id} onToggle={() => toggleDd('btype:' + c.id)} value={c.type} disabled={ro}
                        options={BENEFIT_TYPES.map(([value, label]) => ({ value, label, desc: BENEFIT_DESC[value] }))}
                        onPick={v => pick(x => {
                            const b = x.benefits[i];
                            b.type = v as WinterBenefitType;
                            if (v === 'until') b.dates = b.dates.length ? b.dates.slice(0, 1) : [''];
                            if (v === 'on' && !b.dates.length) b.dates = [''];
                            if (v === 'always') b.dates = [];
                        })} />
                    <button type="button" className="h-soft" title="혜택 삭제" aria-label="혜택 삭제" disabled={ro}
                        onClick={() => upd(x => { x.benefits.splice(i, 1); })} style={{ ...iconBtn, visibility: ro ? 'hidden' : 'visible' }}><XIcon /></button>
                </div>
                {c.type !== 'always' &&
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {c.type === 'until' && <span style={{ fontSize: 14, color: 'var(--ink-700)' }}>마감일 — 이 날까지 결제하면 적용</span>}
                        {c.dates.map((dt, j) => (
                            <div key={j} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 220px) 48px', gap: 8, alignItems: 'center' }}>
                                <DatePicker open={openDd === 'bd:' + c.id + ':' + j} onToggle={() => toggleDd('bd:' + c.id + ':' + j)} value={dt} baseBorder={bd(isYmd(dt))} disabled={ro}
                                    onPick={v => pick(x => { x.benefits[i].dates[j] = v; })} />
                                <button type="button" className="h-soft" title="날짜 삭제" aria-label="날짜 삭제" disabled={ro}
                                    onClick={() => upd(x => { x.benefits[i].dates.splice(j, 1); })}
                                    style={{ ...iconBtn, gridColumn: 3, visibility: c.dates.length > 1 && !ro ? 'visible' : 'hidden' }}><XIcon /></button>
                            </div>
                        ))}
                        {c.type === 'on' && !ro &&
                            <button type="button" className="h-softink" onClick={() => upd(x => { x.benefits[i].dates.push(''); })} style={textBtn}><PlusIcon />날짜 추가</button>
                        }
                    </div>
                }
                <label style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 14, color: 'var(--ink-700)', width: 72 }}>할인율</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <input value={c.pct} readOnly={ro} onChange={e => { const v = digits(e.target.value); upd(x => { x.benefits[i].pct = v; }); }} inputMode="numeric" placeholder="0"
                            style={inputStyle(bd(pctReq(c.pct)), { width: 88, textAlign: 'right' })} />
                        <span style={{ fontSize: 15 }}>%</span>
                    </span>
                </label>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <span style={{ ...label72, lineHeight: '48px' }}>적용 기간</span>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        <button type="button" role="checkbox" aria-checked={!c.periodIds.length} className="h-border"
                            onClick={() => upd(x => { x.benefits[i].periodIds = []; })} style={chipStyle(!c.periodIds.length, 48, 15, 24)}>등록 기간 전체</button>
                        {periods.filter(p => !isPastP(p) || c.periodIds.includes(p.id)).map(p => {
                            const on = c.periodIds.includes(p.id);
                            return (
                                <button key={p.id} type="button" role="checkbox" aria-checked={on} className="h-border"
                                    onClick={() => upd(x => {
                                        const ids = x.benefits[i].periodIds;
                                        x.benefits[i].periodIds = ids.includes(p.id) ? ids.filter(v => v !== p.id) : ids.concat(p.id);
                                    })}
                                    style={chipStyle(on, 48, 15, 24)}>{pName(p) + (isPastP(p) ? ' (지난)' : '')}</button>
                            );
                        })}
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <span style={{ ...label72, lineHeight: '40px' }}>적용 방식</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, minHeight: 40 }}>
                        <div role="radiogroup" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {([[true, '자동 적용'], [false, '직접 적용']] as [boolean, string][]).map(([v, label]) => {
                                const on = (c.auto !== false) === v;
                                return (
                                    <button key={label} type="button" role="radio" aria-checked={on} className="h-border"
                                        onClick={() => upd(x => { x.benefits[i].auto = v; })} style={chipStyle(on, 40, 14, 12)}>{label}</button>
                                );
                            })}
                        </div>
                        <span tabIndex={0} aria-label={autoNote} className="h-soft"
                            onMouseEnter={() => setTipKey('auto-' + c.id)} onMouseLeave={() => setTipKey(k => (k === 'auto-' + c.id ? null : k))}
                            onFocus={() => setTipKey('auto-' + c.id)} onBlur={() => setTipKey(k => (k === 'auto-' + c.id ? null : k))}
                            style={{ position: 'relative', flex: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: '50%', cursor: 'help', outline: 'none' }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B7684" strokeWidth="2" strokeLinecap="round" style={{ display: 'block' }}><circle cx="12" cy="12" r="9"></circle><path d="M12 11v5"></path><path d="M12 8h.01"></path></svg>
                            {tipOn &&
                                <span role="tooltip" style={{ position: 'absolute', right: -6, bottom: 'calc(100% + 8px)', zIndex: 60, width: 'max-content', maxWidth: 240, padding: '8px 10px', borderRadius: 6, background: 'var(--ink-900)', color: '#fff', fontSize: 13, fontWeight: 400, lineHeight: 1.5, letterSpacing: '-0.02em', textAlign: 'left', whiteSpace: 'normal', boxShadow: '0 6px 20px rgba(15, 23, 42, 0.18)', pointerEvents: 'none' }}>{autoNote}</span>
                            }
                        </span>
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <span style={{ ...label72, lineHeight: '40px' }}>제외 지점</span>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
                        <button type="button" className="h-soft" onClick={() => setExclOpen(o => (o === c.id ? null : c.id))} aria-expanded={exOpen}
                            style={{ ...textBtn, marginLeft: -8, color: 'var(--ink-900)' }}>
                            <Chevron rot={exOpen} stroke="currentColor" />{c.excl.length ? c.excl.map(korOf).join(' · ') : '없음'}
                        </button>
                        {exOpen &&
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                {bKeys.map(b => {
                                    const on = c.excl.includes(b);
                                    return (
                                        <button key={b} type="button" role="checkbox" aria-checked={on} className="h-border"
                                            onClick={() => upd(x => { const e = x.benefits[i].excl; x.benefits[i].excl = e.includes(b) ? e.filter(v => v !== b) : e.concat(b); })}
                                            style={{
                                                height: 40, padding: '0 12px', borderRadius: 8, border: `1px solid ${on ? 'var(--ink-900)' : 'var(--ink-200)'}`,
                                                background: on ? 'var(--ink-900)' : '#fff', color: on ? '#fff' : 'var(--ink-900)', fontFamily: 'inherit', fontSize: 14,
                                                fontWeight: 500, letterSpacing: '-0.02em', cursor: 'pointer',
                                            }}>{korOf(b)}</button>
                                    );
                                })}
                            </div>
                        }
                    </div>
                </div>
                <span style={{ fontSize: 14, color: 'var(--ink-900)', lineHeight: 1.5 }}>{explain}</span>
            </div>
        );
    };

    const rootVars = {
        '--ink-900': '#191F28', '--ink-700': '#333D4B', '--ink-500': '#6B7684', '--ink-400': '#8B95A1', '--ink-300': '#B0B8C1',
        '--ink-200': '#E5E8EB', '--hair': '#E5E8EB', '--bg-soft': '#F2F4F6',
    } as React.CSSProperties;

    return (
        <div ref={rootRef} className="wrp-root" style={{
            ...rootVars, maxWidth: 720, marginTop: 24, display: 'flex', flexDirection: 'column', gap: 28,
            color: 'var(--ink-700)', letterSpacing: '-0.02em', paddingBottom: 80,
        }}>
            <style>{HOVER_CSS}</style>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <h2 style={{ margin: 0, fontSize: 28, fontWeight: 300, lineHeight: 1.3, letterSpacing: '-0.025em', color: 'var(--ink-900)' }}>윈터스쿨 등록비 설정</h2>
                {(updatedByName || updatedText) &&
                    <span style={{ fontSize: 13, color: 'var(--ink-500)' }}>마지막 수정: {updatedByName || '알 수 없음'}{updatedText ? ' · ' + updatedText : ''}</span>
                }
                {!loading && !loadError && !fromDb &&
                    <span style={{ fontSize: 13, color: '#B54708' }}>DB 설정이 없어 기본값을 보여주고 있어요</span>
                }
                {!loading && !loadError && !canEdit &&
                    <span style={{ fontSize: 13, color: 'var(--ink-500)' }}>수정 권한이 없어요 (보기만 가능)</span>
                }
            </div>

            {loading && <span style={{ fontSize: 15, color: 'var(--ink-500)' }}>불러오는 중…</span>}
            {!loading && loadError && !d &&
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 15, color: INVALID }}>금액표를 불러오지 못했어요.</span>
                    <button type="button" className="h-border" style={plainBtn} onClick={() => { setLoading(true); load(true); }}>다시 시도</button>
                </div>
            }

            {d && <>
                <div role="tablist" style={{ display: 'flex', gap: 28, borderBottom: '1px solid var(--hair)' }}>
                    {([["price", "금액"], ["period", "기간"], ["benefit", "혜택"]] as [TabKey, string][]).map(([key, label]) => {
                        const active = tab === key;
                        return (
                            <button key={key} type="button" role="tab" aria-selected={active} onClick={() => { setTab(key); setOpenDd(null); }}
                                style={{
                                    height: 44, padding: 0, marginBottom: -1, background: 'none', border: 'none', borderBottom: `1px solid ${active ? ACC : 'transparent'}`,
                                    fontFamily: 'inherit', fontSize: 15, fontWeight: active ? 500 : 400, color: err[key] ? INVALID : active ? ACC : 'var(--ink-500)',
                                    letterSpacing: '-0.02em', cursor: 'pointer',
                                }}>{label}</button>
                        );
                    })}
                </div>

                {tab === 'price' &&
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        <div role="radiogroup" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
                            {([['one', '기간별'], ['all', '전체 보기']] as ['one' | 'all', string][]).map(([k, label]) => (
                                <button key={k} type="button" role="radio" aria-checked={priceView === k} className="h-border" onClick={() => setPriceView(k)}
                                    style={chipStyle(priceView === k, 48, 15, 0)}>{label}</button>
                            ))}
                        </div>
                        <span style={{ fontSize: 13, color: 'var(--ink-500)', lineHeight: 1.6 }}>
                            {priceView === 'all'
                                ? '모든 기간의 지점별 월 금액입니다. 옆으로 밀어서 볼 수 있고, 비워 두면 그 지점의 기본 금액을 씁니다.'
                                : '기간을 골라 지점별 월 금액을 입력합니다. 비워 두면 그 지점의 기본 금액을 씁니다.'}
                        </span>
                        {priceView === 'one' && !!sp &&
                            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 160px) auto', gap: 8, alignItems: 'center' }}>
                                <Dropdown open={openDd === 'pricePeriod'} onToggle={() => toggleDd('pricePeriod')} value={pid}
                                    options={order.map(({ p, i }) => ({ value: p.id, label: pName(p) + (pastFlags[i] ? ' · 지난 기간' : ''), desc: rng(p) }))}
                                    onPick={v => { setOpenDd(null); setPricePid(v); setFillVal(''); }} />
                                <input value={comma(fillVal)} readOnly={ro} onChange={e => setFillVal(digits(e.target.value))}
                                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); applyFill(); } }}
                                    inputMode="numeric" placeholder="금액" style={inputStyle('var(--ink-200)', { textAlign: 'right' })} />
                                <button type="button" className="h-border" onClick={applyFill} disabled={!fillOk || ro}
                                    style={{ ...plainBtn, color: fillOk && !ro ? 'var(--ink-900)' : 'var(--ink-300)', cursor: fillOk && !ro ? 'pointer' : 'default', whiteSpace: 'nowrap' }}>
                                    {picked.length ? '선택 ' + picked.length + '곳에 적용' : '전 지점에 적용'}
                                </button>
                            </div>
                        }
                        <div style={{ overflowX: 'auto' }}>
                            <div style={{ minWidth: priceMinW, display: 'flex', flexDirection: 'column' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: priceGrid, gap: 8, paddingBottom: 12, borderBottom: '1px solid var(--ink-900)' }}>
                                    {priceCols.map((label, ci) => (
                                        <span key={ci} style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink-900)', position: ci === 0 ? 'sticky' : 'static', left: 0, zIndex: 1, background: '#fff', alignSelf: 'stretch', display: 'flex', alignItems: 'flex-end', gap: 10 }}>
                                            {ci === 0 && <CheckBox on={allOn} show={showChk} onClick={() => setPriceSel(allOn ? {} : Object.fromEntries(bKeys.map(b => [b, true])))} />}
                                            {label}
                                        </span>
                                    ))}
                                </div>
                                {bKeys.map((b, ri) => (
                                    <div key={b} style={{ display: 'grid', gridTemplateColumns: priceGrid, gap: 8, alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--hair)' }}>
                                        <span style={{ fontSize: 15, color: 'var(--ink-900)', position: 'sticky', left: 0, zIndex: 1, background: '#fff', alignSelf: 'stretch', display: 'flex', alignItems: 'center', gap: 10 }}>
                                            <CheckBox on={!!priceSel[b]} show={showChk} onClick={() => setPriceSel(s => ({ ...s, [b]: !s[b] }))} />
                                            {korOf(b)}
                                        </span>
                                        <input value={comma(d.prices[b].base)} readOnly={ro} data-pcell={ri + ':0'} enterKeyHint="next" inputMode="numeric"
                                            onChange={e => { const v = digits(e.target.value); upd(x => { x.prices[b].base = v; }); }} onKeyDown={cellKey(ri, 0)}
                                            style={inputStyle(bd(baseOk(d.prices[b].base)), { textAlign: 'right' })} />
                                        {priceColPeriods.map(({ p }, k) => {
                                            const v = d.prices[b].periods[p.id] ?? '';
                                            return (
                                                <input key={p.id} value={comma(v)} readOnly={ro} data-pcell={ri + ':' + (k + 1)} enterKeyHint="next" inputMode="numeric" placeholder="기본 금액"
                                                    onChange={e => { const nv = digits(e.target.value); upd(x => { x.prices[b].periods[p.id] = nv; }); }} onKeyDown={cellKey(ri, k + 1)}
                                                    style={inputStyle(bd(optPriceOk(v)), { textAlign: 'right' })} />
                                            );
                                        })}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                }

                {tab === 'period' &&
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '0 0 12px', borderBottom: '1px solid var(--ink-900)' }}>
                            <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink-900)' }}>기간</span>
                            <span style={{ fontSize: 13, color: 'var(--ink-500)', lineHeight: 1.6 }}>기간마다 대상과 날짜만 정합니다. 금액은 [금액] 탭, 할인은 [혜택] 탭에서 정합니다. 날짜가 겹치면 위에 있는 기간이 우선하고, 어느 기간에도 없는 날은 기본 금액을 씁니다.</span>
                        </div>
                        {periods.map((p, i) => (pastFlags[i] ? null : renderPeriod(p, i, i + 1)))}
                        {nPast > 0 &&
                            <button type="button" className="h-softink" onClick={() => setShowPast(v => !v)} style={{ ...textBtn, marginTop: 12 }}>
                                <Chevron rot={showPast} stroke="currentColor" />{showPast ? '지난 기간 접기' : '지난 기간 ' + nPast + '개 보기'}
                            </button>
                        }
                        {nPast > 0 && showPast &&
                            <div style={{ display: 'flex', flexDirection: 'column', borderTop: '1px solid var(--hair)' }}>
                                {periods.map((p, i) => (pastFlags[i] ? renderPeriod(p, i, i + 1) : null))}
                            </div>
                        }
                        {!ro &&
                            <button type="button" className="h-dashed" style={dashedBtn} onClick={() => upd(x => {
                                const id = uid('p');
                                x.periods.push({ id, name: '', target: 'all', ranges: [{ start: '', end: '' }] });
                                Object.values(x.prices).forEach(r => { r.periods[id] = ''; });
                            })}><PlusIcon size={18} />기간 추가</button>
                        }
                    </div>
                }

                {tab === 'benefit' &&
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '0 0 12px', borderBottom: '1px solid var(--ink-900)' }}>
                            <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink-900)' }}>혜택</span>
                            <span style={{ fontSize: 13, color: 'var(--ink-500)', lineHeight: 1.6 }}>혜택마다 조건, 할인율, 적용 기간을 정합니다. 결제일(링크 발송일) 기준으로 판단하며, 같은 날에 할인이 여러 개 겹치면 가장 큰 할인 하나만 적용합니다 (모달의 추가 할인율 포함).</span>
                        </div>
                        {d.benefits.map((c, i) => renderBenefit(c, i))}
                        {!ro &&
                            <button type="button" className="h-dashed" style={dashedBtn} onClick={() => upd(x => {
                                x.benefits.push({ id: uid('b'), name: '', type: 'until', dates: [''], pct: '', periodIds: [], excl: [], auto: true });
                            })}><PlusIcon size={18} />혜택 추가</button>
                        }
                    </div>
                }

                {errMsgs.length > 0 &&
                    <div role="alert" style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '12px 16px', borderRadius: 8, background: '#FDECEA' }}>
                        {errMsgs.map(em => <span key={em} style={{ fontSize: 13, lineHeight: 1.5, color: '#B42318' }}>{em}</span>)}
                    </div>
                }
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                    <button type="button" className="h-border" onClick={resetToDefault} disabled={ro || !defaults}
                        style={{ ...plainBtn, color: ro ? 'var(--ink-300)' : 'var(--ink-900)', cursor: ro ? 'default' : 'pointer' }}>초기값으로 되돌리기</button>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {ro && <span style={{ fontSize: 13, color: 'var(--ink-500)', marginRight: 4 }}>수정 권한이 없어요</span>}
                        <button type="button" className="h-border" onClick={cancelEdit} disabled={ro}
                            style={{ ...plainBtn, color: ro ? 'var(--ink-300)' : 'var(--ink-900)', cursor: ro ? 'default' : 'pointer' }}>취소</button>
                        <button type="button" className="h-save" onClick={save} disabled={!saveOn}
                            style={{
                                height: 48, padding: '0 24px', borderRadius: 8, border: `1px solid ${saveOn ? ACC : 'var(--bg-soft)'}`, background: saveOn ? ACC : 'var(--bg-soft)',
                                color: saveOn ? '#fff' : 'var(--ink-300)', fontFamily: 'inherit', fontSize: 15, fontWeight: 500, letterSpacing: '-0.02em', cursor: saveOn ? 'pointer' : 'default',
                            }}>{saving ? '저장 중…' : '저장'}</button>
                    </div>
                </div>
            </>}
        </div>
    );
};

export default WinterRegisterPrice;
